import { environment } from '../../../environments/environment';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  AdminChallengeContent,
  Challenge,
  ChallengeHistory,
  DailyChallengeContent,
  MonthlyChallengeContent
} from './models/challenges.model';
import { Observable } from 'rxjs';
import {
  AdminChallengeResponse,
  DailyChallenge,
  HistoryUserChallenge,
  MonthlyChallenge,
  PersonalChallengeResponse
} from './models/challenges.response';
import { map } from 'rxjs/operators';
import { ChallengesFactory } from './challenges.factory';
import { UserChallengeGoal } from './models/challenges.request';
import { HttpMethods } from 'src/app/enums/httpMethods.enum';

@Injectable({
  providedIn: 'root'
})
export class ChallengesService {
  private readonly challengesFactory = inject(ChallengesFactory);
  private readonly http = inject(HttpClient);
  private readonly apiUrlPersonalDaily = `${environment.apiUrl}/challenges/personal/daily`;
  private readonly apiUrlPersonalMonthly = `${environment.apiUrl}/challenges/personal/monthly`;

  getAdminChallenges(): Observable<{
    global: Challenge<AdminChallengeContent>;
    assigned: Challenge<AdminChallengeContent>;
  }> {
    const url = `${environment.apiUrl}/challenges/admin-defined/active`;

    return this.http.get<AdminChallengeResponse>(url).pipe(
      map(({ global, assigned }) => ({
        global: global ? this.challengesFactory.buildAdminChallenge(global, true) : undefined,
        assigned: assigned ? this.challengesFactory.buildAdminChallenge(assigned) : undefined,
      }))
    );
  }

  getUserChallenges(): Observable<{
    daily: Challenge<DailyChallengeContent>;
    monthly: Challenge<MonthlyChallengeContent>;
  }> {
    const url = `${environment.apiUrl}/challenges/personal/active`;

    return this.http.get<PersonalChallengeResponse>(url).pipe(
      map(({ daily, monthly }) => ({
        daily: daily ? this.challengesFactory.buildDailyChallenge(daily) : undefined,
        monthly: monthly ? this.challengesFactory.buildMonthlyChallenge(monthly) : undefined,
      }))
    );
  }

  postUserDailyChallenge(goal: UserChallengeGoal): Observable<Challenge<DailyChallengeContent>> {
    return this.postOrPutDailyChallenge(HttpMethods.POST, goal);
  }

  putUserDailyChallenge(goal: UserChallengeGoal): Observable<Challenge<DailyChallengeContent>> {
    return this.postOrPutDailyChallenge(HttpMethods.PUT, goal);
  }

  postUserMonthlyChallenge(goal: UserChallengeGoal): Observable<Challenge<MonthlyChallengeContent>> {
    return this.postOrPutMonthlyChallenge(HttpMethods.POST, goal);
  }

  putUserMonthlyChallenge(goal: UserChallengeGoal): Observable<Challenge<MonthlyChallengeContent>> {
    return this.postOrPutMonthlyChallenge(HttpMethods.PUT, goal);
  }

  deleteUserDailyChallenge(): Observable<void> {
    return this.http.delete<void>(this.apiUrlPersonalDaily);
  }

  deleteUserMonthlyChallenge(): Observable<void> {
    return this.http.delete<void>(this.apiUrlPersonalMonthly);
  }

  getHistoryChallenges(pageSize = 10, page = 1): Observable<ChallengeHistory> {
    const url = `${environment.apiUrl}/challenges/admin-defined/history`;
    const params = new HttpParams().set('pageSize', pageSize).set('page', page);

    return this.http.get<HistoryUserChallenge>(url, { params })
    .pipe(map((response) => this.challengesFactory.buildHistoryUserChallenge(response)));
  }

  private postOrPutDailyChallenge(
    httpMethod: HttpMethods,
    goal: UserChallengeGoal
  ): Observable<Challenge<DailyChallengeContent>> {
    return this.http
      .request<DailyChallenge>(httpMethod, this.apiUrlPersonalDaily, { body: goal })
      .pipe(map((response) => this.challengesFactory.buildDailyChallenge(response)));
  }

  private postOrPutMonthlyChallenge(
    httpMethod: HttpMethods,
    target: UserChallengeGoal
  ): Observable<Challenge<MonthlyChallengeContent>> {
    return this.http
      .request<MonthlyChallenge>(httpMethod, this.apiUrlPersonalMonthly, { body: target })
      .pipe(map((response) => this.challengesFactory.buildMonthlyChallenge(response)));
  }
}
